import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, FileText, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '@/lib/utils';

const FileUpload = ({ value, onChange, label = "PDF File" }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Max 10MB');
      return;
    }

    setError('');
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('steward_token');
      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      onChange(`${API_URL}/files/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange('');
    setError('');
  };

  return (
    <div data-testid="file-upload">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>

      {value ? (
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <FileText className="h-8 w-8 text-red-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-800">PDF uploaded</p>
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline truncate block">
              View file
            </a>
          </div>
          <div className="flex gap-1">
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Replace'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={handleRemove} data-testid="file-remove-btn">
              <X className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-5 text-center cursor-pointer hover:border-[#1a2a5e] hover:bg-slate-50 transition-colors"
          onClick={() => fileRef.current?.click()}
          data-testid="file-upload-dropzone"
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-7 w-7 text-[#1a2a5e] animate-spin mb-2" />
              <p className="text-sm text-gray-500">Uploading PDF...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="h-7 w-7 text-gray-400 mb-2" />
              <p className="text-sm font-medium text-gray-700">Click to upload PDF brochure</p>
              <p className="text-xs text-gray-400 mt-1">PDF only, max 10MB</p>
            </div>
          )}
        </div>
      )}

      <Input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste PDF URL"
        className="mt-2"
        data-testid="file-url-input"
      />

      <input type="file" ref={fileRef} onChange={handleUpload} accept=".pdf" className="hidden" />

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default FileUpload;
